import {ApiClient as Api} from '@hconnect/apiclient'
import {KpiType} from '@hconnect/common/types'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {Moment} from 'moment-timezone'
import {stringify} from 'qs'
import {useEffect} from 'react'

import {KpisQueryKey, type MonthlyTrendsKpisDto} from '../../types'

type MonthlyTrendsByTypesParams = {
  plantId: string
  from: Moment
  to: Moment
  kpiTypes: KpiType[]
}

type Options = Omit<UseQueryOptions<MonthlyTrendsKpisDto[], AxiosError>, 'queryKey' | 'queryFn'> & {
  onError?: (error: AxiosError) => void
}

export const useKpisMonthlyTrendsByTypes = (
  {plantId, ...searchParams}: MonthlyTrendsByTypesParams,
  options?: Options
) => {
  const {from, to, kpiTypes} = searchParams
  const params = {
    kpiTypes,
    fromYear: from.year(),
    fromMonth: from.month() + 1, // Adjusting because Moment.js months are 0-indexed
    toYear: to.year(),
    toMonth: to.month() + 1
  }

  const query = useQuery<MonthlyTrendsKpisDto[], AxiosError>({
    queryKey: [
      KpisQueryKey.MonthlyTrendsByTypes,
      plantId,
      kpiTypes,
      from.toISOString(),
      to.toISOString()
    ],
    queryFn: async ({signal}) => {
      const path = `/kpi/actual-production-values/${plantId}/kpis/monthly-trends`
      const response = await Api.axiosInstance.get<MonthlyTrendsKpisDto[]>(path, {
        params,
        paramsSerializer: (params) => stringify(params, {arrayFormat: 'repeat'}),
        signal
      })
      return response.data
    },
    ...options
  })

  const onError = options?.onError

  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  return query
}
